import type { DiagnosticOption } from "src/services/diagnosticService";
import { ContentBlocks } from "src/components/assessment/ContentBlocks";
import { cn } from "src/lib/utils";

interface OptionsMCQProps {
  multiple: boolean;
  options: DiagnosticOption[];
  value: string | string[];
  onChange: (value: string | string[]) => void;
}

function optionBody(option: DiagnosticOption) {
  return <ContentBlocks content={option.content ?? option.content_block ?? null} />;
}

export function OptionsMCQ({ multiple, options, value, onChange }: OptionsMCQProps) {
  const selected = Array.isArray(value) ? value : [];

  if (!multiple) {
    const current = typeof value === "string" ? value : "";
    return (
      <fieldset>
        <legend className="sr-only">Choose one answer</legend>
        <div role="radiogroup" className="flex flex-col gap-2.5">
          {options.map((option, idx) => {
            const checked = current === option.id;
            const letter = String.fromCharCode(65 + idx);
            return (
              <label
                key={option.id}
                className={cn(
                  "group flex cursor-pointer items-start gap-3 rounded-xl border p-3 transition-all duration-150",
                  checked
                    ? "border-purple-600 bg-purple-50/70 shadow-2xs"
                    : "border-slate-200 bg-white hover:border-purple-300 hover:bg-purple-50/20"
                )}
              >
                <input
                  type="radio"
                  name="diagnostic-mcq"
                  className="sr-only"
                  checked={checked}
                  onChange={() => onChange(option.id)}
                />
                <span
                  className={cn(
                    "flex h-6 w-6 shrink-0 items-center justify-center rounded-md text-xs transition-colors",
                    checked
                      ? "bg-purple-600 font-bold text-white shadow-2xs"
                      : "border border-slate-300 bg-white font-semibold text-slate-600 group-hover:border-purple-400 group-hover:text-purple-700"
                  )}
                  aria-hidden="true"
                >
                  {letter}
                </span>
                <span className={cn("min-w-0 flex-1 pt-0.5 text-xs sm:text-sm", checked ? "font-semibold text-purple-950" : "font-normal text-slate-800")}>
                  {optionBody(option)}
                </span>
              </label>
            );
          })}
        </div>
      </fieldset>
    );
  }

  return (
    <fieldset>
      <legend className="mb-2 text-xs font-medium text-slate-600">Select every answer that applies.</legend>
      <div className="flex flex-col gap-2.5">
        {options.map((option, idx) => {
          const checked = selected.includes(option.id);
          const letter = String.fromCharCode(65 + idx);
          return (
            <label
              key={option.id}
              className={cn(
                "group flex cursor-pointer items-start gap-3 rounded-xl border p-3 transition-all duration-150",
                checked
                  ? "border-purple-600 bg-purple-50/70 shadow-2xs"
                  : "border-slate-200 bg-white hover:border-purple-300 hover:bg-purple-50/20"
              )}
            >
              <input
                type="checkbox"
                className="sr-only"
                checked={checked}
                onChange={() => {
                  const next = checked
                    ? selected.filter((id) => id !== option.id)
                    : [...selected, option.id];
                  onChange(next);
                }}
              />
              <span
                className={cn(
                  "flex h-6 w-6 shrink-0 items-center justify-center rounded-md text-xs transition-colors",
                  checked
                    ? "bg-purple-600 font-bold text-white shadow-2xs"
                    : "border border-slate-300 bg-white font-semibold text-slate-600 group-hover:border-purple-400 group-hover:text-purple-700"
                )}
                aria-hidden="true"
              >
                {letter}
              </span>
              <span className={cn("min-w-0 flex-1 pt-0.5 text-xs sm:text-sm", checked ? "font-semibold text-purple-950" : "font-normal text-slate-800")}>
                {optionBody(option)}
              </span>
            </label>
          );
        })}
      </div>
    </fieldset>
  );
}

export default OptionsMCQ;
